import { useState, type ReactElement } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { BadgeCheck, FilePen } from 'lucide-react';
import type { CartaoControleEdicaoView } from '@pieltec/contracts';
import { fatosCabecalhoCp } from '../../lib/rastreamento/edicao-cp';
import { etapasDoRoteiro, type EtapaDoRoteiro } from '../../lib/rastreamento/etapa-roteiro';
import '../../styles/cartao-controle.css';

export type EtapaDoCartao = EtapaDoRoteiro;
export interface PaginaRoteiro { readonly atual: number; readonly total: number; readonly etapas: readonly EtapaDoCartao[]; }

export function paginasDoRoteiro(etapas: readonly unknown[], tamanho = 8): readonly PaginaRoteiro[] {
  const normalizadas = etapasDoRoteiro(etapas);
  if (normalizadas.length === 0) return [{ atual: 1, total: 1, etapas: [] }];
  const total = Math.ceil(normalizadas.length / tamanho);
  return Array.from({ length: total }, (_, index) => ({ atual: index + 1, total, etapas: normalizadas.slice(index * tamanho, (index + 1) * tamanho) }));
}

/** O QR contém a rota interna e somente o token opaco da edição; não concede acesso. */
export function urlResumoQr(qrToken: string, origem = window.location.origin): string {
  const url = new URL('/producao/resumo-lote', origem);
  url.searchParams.set('qr', qrToken);
  return url.toString();
}

export async function prepararImpressao(acoes: { readonly registrarAbertura: () => Promise<void>; readonly imprimir: () => void; readonly aguardarPronto?: () => Promise<void> }): Promise<void> {
  await acoes.registrarAbertura();
  await acoes.aguardarPronto?.();
  acoes.imprimir();
}

function textoQuantidade(edicao: CartaoControleEdicaoView): string {
  const quantidade = edicao.snapshot.op.quantidade;
  return quantidade.estado === 'NAO_INFORMADA' ? 'Quantidade não informada' : `${quantidade.valor ?? 'Não informada'} ${quantidade.unidade ?? ''}`.trim();
}

/** 28 mm a 96 dpi (área mínima legível do QR impresso). */
export const TAMANHO_QR_PX = 112;

export interface CartaoControleA4Props {
  readonly edicao: CartaoControleEdicaoView;
  readonly aoPrepararImpressao: (id: string) => Promise<void>;
  readonly historico?: readonly CartaoControleEdicaoView[];
}

export function CartaoControleA4({ edicao, aoPrepararImpressao, historico = [] }: CartaoControleA4Props): ReactElement {
  const [preparando, setPreparando] = useState(false);
  const paginas = paginasDoRoteiro(edicao.snapshot.roteiro.etapas);
  const { lote, op, revisao, tratamento } = edicao.snapshot;
  const fatos = fatosCabecalhoCp(edicao, historico);
  const IconeSituacao = fatos.situacao === 'Rascunho' ? FilePen : BadgeCheck;
  const imprimir = async (): Promise<void> => {
    if (preparando) return;
    setPreparando(true);
    try {
      await prepararImpressao({
        registrarAbertura: () => aoPrepararImpressao(edicao.id),
        aguardarPronto: async () => { await document.fonts?.ready; },
        imprimir: () => window.print(),
      });
    } finally { setPreparando(false); }
  };
  return <section className="cartao-controle" aria-label={`Cartão de controle edição ${edicao.sequencia}`}>
    {paginas.map((pagina) => <article className="cartao-controle__folha" key={pagina.atual}>
      <header className="cartao-controle__cabecalho">
        <div>
          <p className="cartao-controle__selo">Pieltec · Cartão de controle</p>
          <p className="cartao-controle__identificacao">
            <span className="cartao-controle__situacao" data-situacao={fatos.situacao}><IconeSituacao size={16} aria-hidden="true" />{fatos.situacao}</span>
            <span className="cartao-controle__cp">{fatos.identificador}</span>
            <span className="cartao-controle__data">{fatos.dataHora}</span>
          </p>
          {fatos.substituidaPor !== null && <p className="cartao-controle__substituicao">Substituído por edição {fatos.substituidaPor}</p>}
          {fatos.avisoRascunho && <p className="cartao-controle__rascunho">{fatos.avisoRascunho}</p>}
          <h1>Lote {lote.codigo}</h1>
          <p>OP {op.numero ?? 'não informada'} · Edição {edicao.sequencia}</p>
        </div>
        <figure className="cartao-controle__qr">
          <QRCodeSVG value={urlResumoQr(edicao.qrToken)} size={TAMANHO_QR_PX} level="M" includeMargin aria-label={`QR da edição ${fatos.identificador}`} />
          <figcaption>{fatos.identificador}</figcaption>
        </figure>
      </header>
      <dl className="cartao-controle__dados">
        <div><dt>Item</dt><dd>{lote.itemId}</dd></div><div><dt>Status da OP</dt><dd>{op.status ?? 'Não informada'}</dd></div>
        <div><dt>Quantidade</dt><dd>{textoQuantidade(edicao)}</dd></div><div><dt>Prazo</dt><dd>{op.prazo ?? 'Não informado'}</dd></div>
        <div><dt>Revisão</dt><dd>{revisao.codigo ?? 'Sem desenho cadastrado'}</dd></div><div><dt>Proveniência</dt><dd>{revisao.estado === 'FIXADA' ? 'Revisão fixada no lote' : 'Revisão provisória'}</dd></div>
        {tratamento && <><div><dt>Tratamento do pedido</dt><dd>{tratamento.codigo} · v{tratamento.versao} · {tratamento.nome}</dd></div><div><dt>Tinta / cor</dt><dd>{[tratamento.tipoTinta, tratamento.cor].filter(Boolean).join(' · ') || 'Não especificada'}</dd></div>
        <div><dt>Isolamento</dt><dd>{tratamento.exigeIsolamento ? 'Obrigatório' : 'Não exigido'}</dd></div><div><dt>Requisitos</dt><dd>{tratamento.requisitos ?? 'Sem requisitos adicionais'}</dd></div></>}
      </dl>
      <section className="cartao-controle__roteiro" aria-label="Roteiro de produção"><h2>Roteiro</h2>{pagina.etapas.length ? <ol>{pagina.etapas.map((etapa) => <li key={`${etapa.ordem}-${etapa.nome}`}><strong>{etapa.ordem}. {etapa.nome}</strong>{etapa.detalhe && <span>{etapa.detalhe}</span>}</li>)}</ol> : <p>Roteiro não informado.</p>}</section>
      <footer>Edição preparada em {fatos.dataHora} · {pagina.total > 1 ? `Continuação ${pagina.atual}/${pagina.total}` : 'Página 1/1'}</footer>
    </article>)}
    <button className="cartao-controle__imprimir" type="button" disabled={preparando} onClick={() => void imprimir()}>{preparando ? 'Preparando impressão…' : 'Imprimir cartão de controle'}</button>
    <p className="cartao-controle__aviso" role="status">A edição foi preparada para impressão. Abrir ou fechar o diálogo não confirma saída física do papel.</p>
  </section>;
}
